import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createLayoutApi,
  deleteLayoutApi,
  getLayoutApi,
  getLayoutsApi,
  updateLayoutApi,
} from "../api/layoutsApi";
import {
  catalogQueryKey,
  type CatalogScope,
} from "../api/catalogScope";
import type { LayoutId } from "@KassaroAB/shared/types/ids";
import type {
  RestCreateLayoutType,
  RestUpdateLayoutType,
} from "@KassaroAB/shared/types/layout";

const layoutsQueryKey = (scope: CatalogScope | null) =>
  catalogQueryKey(scope, "layouts");

const invalidateLayoutQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  scope: CatalogScope | null,
) => {
  queryClient.invalidateQueries({ queryKey: layoutsQueryKey(scope) });
};

export const useGetLayoutsQuery = (scope: CatalogScope | null) => {
  return useQuery({
    queryKey: layoutsQueryKey(scope),
    queryFn: () => getLayoutsApi(scope!),
    enabled: !!scope,
  });
};

export const useGetLayoutQuery = (
  scope: CatalogScope | null,
  layoutId: LayoutId | null | undefined,
) => {
  return useQuery({
    queryKey: [...layoutsQueryKey(scope), layoutId],
    queryFn: () => getLayoutApi(scope!, layoutId!),
    enabled: !!scope && !!layoutId,
  });
};

export const useCreateLayoutMutation = (scope: CatalogScope | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (layout: RestCreateLayoutType) =>
      createLayoutApi(scope!, layout),
    onSuccess: () => {
      invalidateLayoutQueries(queryClient, scope);
    },
  });
};

export const useUpdateLayoutMutation = (scope: CatalogScope | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      layoutId,
      layout,
    }: {
      layoutId: LayoutId;
      layout: RestUpdateLayoutType;
    }) => updateLayoutApi(scope!, layoutId, layout),
    onSuccess: () => {
      invalidateLayoutQueries(queryClient, scope);
    },
  });
};

export const useDeleteLayoutMutation = (scope: CatalogScope | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (layoutId: LayoutId) => deleteLayoutApi(scope!, layoutId),
    onSuccess: () => {
      invalidateLayoutQueries(queryClient, scope);
    },
  });
};
